/**
 * Auth hook — httpOnly cookie-based. The JWT lives server-side in a Set-Cookie header;
 * JS never touches the token. User state is loaded by calling /me on mount.
 */

import { useEffect, useState, useCallback } from 'react';

export type AuthUser = {
  id: string;
  email: string;
  is_verified: boolean;
  totp_enabled: boolean;
  jackpot_winner: boolean;
  created_at: string;
  verification_deadline: string | null;
};

/** Fetch with credentials so the httpOnly cookie is sent automatically. */
export async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };
  return fetch(path, { ...init, headers, credentials: 'include' });
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null | undefined>(undefined); // undefined = loading
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const resp = await authedFetch('/api/v1/auth/me');
      if (resp.ok) {
        setUser(await resp.json());
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await authedFetch('/api/v1/auth/logout', { method: 'POST' });
    } finally {
      setUser(null);
    }
  }, []);

  return { user, loading, logout, refresh };
}
